import React, { useMemo, useState } from 'react';
import { AlertTriangle, Link2, X } from 'lucide-react';
import { useEngagement } from '../hooks/useEngagement';
import { useSharedStream } from '../hooks/useSharedStream';
import {
  buildStreamPainFactsModel,
  linkPainToTarget,
  replaceStreamPainLinks,
  unlinkPainLink,
  type PainFact,
  type PainLinkTarget,
  type StreamPainLink,
} from '../utils/streamPainFactsModel';
import type { StreamWorkspaceReadPanelProps } from './streamMappingWorkspaceTypes';
import '../styles/StreamPainFactsPanel.css';

function targetValue(target: PainLinkTarget): string {
  return `${target.kind}|${target.id}`;
}

function sourceLabel(fact: PainFact): string {
  if (fact.source === 'organisation') return 'Confirmed organisation pain';
  if (fact.source === 'missing-source') return 'Missing source - review';
  return 'Review suggestion';
}

function sourceTone(fact: PainFact): string {
  if (fact.source === 'organisation') return 'confirmed';
  if (fact.source === 'missing-source') return 'missing';
  return 'candidate';
}

function linkCountText(count: number): string {
  return `${count} placed`;
}

function unresolvedText(count: number): string {
  return `${count} unresolved`;
}

const StreamPainFactsPanel: React.FC<StreamWorkspaceReadPanelProps> = ({ stream, session, readOnly = false }) => {
  const { snapshot, saveStreams, currentEngagementId } = useEngagement();
  const shared = useSharedStream(currentEngagementId, stream.sharedProcesses ? stream.id : null);
  const [selectedTargets, setSelectedTargets] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const readModel = useMemo(
    () =>
      buildStreamPainFactsModel({
        organisation: snapshot?.organisation,
        stream,
        session,
        ...(stream.sharedProcesses ? { sharedSteps: shared.shared?.steps || [] } : {}),
      }),
    [snapshot?.organisation, stream, session, shared.shared],
  );

  const factsById = useMemo(
    () => new Map(readModel.facts.map((fact) => [fact.id, fact])),
    [readModel.facts],
  );

  const persistLinks = async (nextLinks: StreamPainLink[], savingKey: string) => {
    if (readOnly) return;
    setSaving(savingKey);
    setError(null);
    try {
      const streamsSubDoc = snapshot?.streams ?? { streams: [stream] };
      await saveStreams(replaceStreamPainLinks(streamsSubDoc, stream.id, nextLinks));
    } catch {
      setError('Pain links could not be saved. Try again before leaving this stream.');
    } finally {
      setSaving(null);
    }
  };

  const chooseTarget = (fact: PainFact): PainLinkTarget | null => {
    const selected = selectedTargets[fact.id];
    const value = selected ?? targetValue(readModel.targetOptions[0]);
    return readModel.targetOptions.find((target) => targetValue(target) === value) ?? null;
  };

  const handleLink = (fact: PainFact) => {
    const target = chooseTarget(fact);
    if (!target) return;
    void persistLinks(linkPainToTarget(readModel.links, fact, target), fact.id);
  };

  const handleRemoveLink = (link: StreamPainLink) => {
    void persistLinks(unlinkPainLink(readModel.links, link.id), link.id);
  };

  const hasPain = readModel.facts.length > 0;

  return (
    <section className="smw-leaf spf-panel" data-testid="stream-pain-facts-panel">
      <div className="smw-panel-head">
        <div>
          <h2>Pain points</h2>
          <p>Place what is not working beside the steps, waits, and outcomes it affects.</p>
        </div>
        <AlertTriangle size={18} aria-hidden="true" />
      </div>

      {stream.sharedProcesses ? readModel.links.filter(link => link.target.kind !== 'outcome' && !shared.shared?.steps.some(step => step.stepId === link.target.id)).map(link => <p role="alert" key={link.id}>Review retained pain reference: {link.painTitle} — its original step is not in the current stream. It has not been reassigned.</p>) : null}

      <div className="spf-summary" aria-label="Pain point placement summary">
        <span>{linkCountText(readModel.links.length)}</span>
        <span>{unresolvedText(readModel.unresolved.length)}</span>
        <span>{readModel.candidates.length} suggestions</span>
      </div>

      {error ? (
        <div className="spf-alert" role="alert">
          {error}
        </div>
      ) : null}

      {!hasPain ? (
        <div className="spf-empty">
          Ask what is not working, where people wait, and what keeps coming back.
        </div>
      ) : null}

      {readModel.links.length > 0 ? (
        <section className="spf-section" aria-labelledby="spf-placed-heading">
          <div className="spf-section-head">
            <h3 id="spf-placed-heading">Placed pain evidence</h3>
          </div>
          <ul className="spf-link-list">
            {readModel.links.map((link) => {
              const fact = factsById.get(link.painFactId);
              const title = fact?.title ?? link.painTitle;
              return (
                <li key={link.id} className="spf-link-row">
                  <div>
                    <span className="spf-target-label">{link.target.label}</span>
                    <strong>{title}</strong>
                  </div>
                  <button
                    type="button"
                    className="spf-icon-button"
                    aria-label={`Remove pain link ${title} from ${link.target.label}`}
                    onClick={() => handleRemoveLink(link)}
                    disabled={readOnly || saving === link.id}
                  >
                    <X size={16} aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {hasPain ? (
        <section className="spf-section" aria-labelledby="spf-unresolved-heading">
          <div className="spf-section-head">
            <h3 id="spf-unresolved-heading">Unresolved pain</h3>
            <span>{unresolvedText(readModel.unresolved.length)}</span>
          </div>
          {readModel.unresolved.length > 0 ? (
            <ul className="spf-unresolved-list">
              {readModel.unresolved.map((fact) => (
                <li key={fact.id}>{fact.title}</li>
              ))}
            </ul>
          ) : (
            <p className="spf-done">Current pain evidence has been placed.</p>
          )}
        </section>
      ) : null}

      {hasPain ? (
        <section className="spf-section" aria-labelledby="spf-facts-heading">
          <div className="spf-section-head">
            <h3 id="spf-facts-heading">Pain facts</h3>
          </div>
          <div className="spf-fact-list">
            {readModel.facts.map((fact) => {
              const selectedValue =
                selectedTargets[fact.id] ?? targetValue(readModel.targetOptions[0]);
              return (
                <article className="spf-fact" key={fact.id}>
                  <div className="spf-fact-topline">
                    <span className={`spf-source spf-source--${sourceTone(fact)}`}>
                      {sourceLabel(fact)}
                    </span>
                    {fact.candidateCount && fact.candidateCount > 1 ? (
                      <span className="spf-repeat">Mentioned {fact.candidateCount} times</span>
                    ) : null}
                  </div>
                  <h4>{fact.title}</h4>
                  {fact.detail ? <p>{fact.detail}</p> : null}
                  {fact.whoFeelsIt ? <p>Felt by {fact.whoFeelsIt}</p> : null}
                  {fact.source === 'missing-source' ? (
                    <p>Review this link because the original organisation pain point is missing.</p>
                  ) : null}
                  {readModel.targetOptions.length > 0 && fact.source !== 'missing-source' ? (
                    <div className="spf-place-row">
                      <select
                        aria-label={`Target for ${fact.title}`}
                        value={selectedValue}
                        onChange={(event) =>
                          setSelectedTargets((current) => ({
                            ...current,
                            [fact.id]: event.target.value,
                          }))
                        }
                      >
                        {readModel.targetOptions.map((target) => (
                          <option key={targetValue(target)} value={targetValue(target)}>
                            {target.label}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="spf-link-button"
                        aria-label={`Link ${fact.title}`}
                        onClick={() => handleLink(fact)}
                        disabled={readOnly || saving === fact.id}
                      >
                        <Link2 size={15} aria-hidden="true" />
                        Link
                      </button>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        </section>
      ) : null}
    </section>
  );
};

export default StreamPainFactsPanel;
